import { createStyles, fade, makeStyles, Theme } from '@material-ui/core/styles';

/** Styles for the CENTERLINE AI row on the Consultant card and the review pane, from theme tokens. */
const useReviewStyles = makeStyles((theme: Theme) =>
  createStyles({
    /* ---- the row ---- */
    row: {
      backgroundColor: fade(theme.palette.primary.main, 0.05),
      '& > td:first-child': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
      },
    },
    reviewer: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
    mark: { width: 28, height: 28, borderRadius: 6, flexShrink: 0 },
    name: { fontWeight: 500, whiteSpace: 'nowrap' },
    aiChip: {
      height: 20,
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    muted: { color: theme.palette.text.secondary },
    actionChip: { fontWeight: 500 },

    /* ---- the pane ---- */
    drawerPaper: {
      width: 640,
      maxWidth: '100vw',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.background.paper,
      // CssBaseline gives <body> the theme's body2 (uppercase, medium weight); the chat page undoes
      // both on its root (styles.ts `page`), and so does the pane.
      fontFamily: 'Roboto, sans-serif',
      textTransform: 'none',
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
    // The brand gradient the dashboard card headers use (e.g. dashboard/DashboardSearch.tsx).
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
      color: theme.palette.common.white,
    },
    headerChip: {
      height: 20,
      fontSize: 11,
      fontWeight: 500,
      color: theme.palette.common.white,
      borderColor: fade(theme.palette.common.white, 0.7),
    },
    headerClose: { color: theme.palette.common.white },
    headerTitle: {
      flexGrow: 1,
      minWidth: 0,
      fontWeight: 500,
      fontSize: 16,
      lineHeight: '24px',
      color: theme.palette.common.white,
    },
    strip: {
      flexShrink: 0,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1, 2),
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: fade(theme.palette.primary.main, 0.03),
    },
    stripLabel: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(0.5),
    },
    fileChip: { maxWidth: 220 },
    fileChipPending: { borderStyle: 'dashed' },
    body: {
      flexGrow: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    thread: { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' },
    status: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1.5),
      padding: theme.spacing(4, 3),
      textAlign: 'center',
    },
    statusText: { maxWidth: 440 },
    statusActions: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: theme.spacing(1),
    },
    actionBar: {
      flexShrink: 0,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    snips: {
      flexShrink: 0,
      padding: theme.spacing(1, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      maxHeight: 150,
      overflowY: 'auto',
    },
    snipRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.5),
    },
    snip: {
      padding: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      background: 'transparent',
      cursor: 'pointer',
      lineHeight: 0,
      '& img': { height: 72, display: 'block' },
      '&:hover': { borderColor: theme.palette.primary.main },
    },
    snipFull: { maxWidth: '100%', display: 'block', margin: '0 auto' },
    note: {
      flexShrink: 0,
      padding: theme.spacing(0, 2, 1),
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
  }),
);

export default useReviewStyles;
